<template>
  <Teleport to="body">
    <Transition name="site-search-fade">
      <div
        v-if="open"
        class="fixed inset-0 z-[80] flex items-center justify-center p-3 sm:p-6"
        role="presentation"
      >
        <button
          type="button"
          class="absolute inset-0 bg-gray-950/50 backdrop-blur-[2px]"
          aria-label="Închide căutarea"
          @click="closeSearch"
        />

        <div
          ref="dialogRef"
          role="dialog"
          aria-modal="true"
          aria-label="Caută produse"
          class="relative z-10 flex w-full max-w-2xl max-h-[min(86dvh,720px)] flex-col overflow-hidden rounded-2xl bg-default shadow-2xl ring-1 ring-black/10 dark:ring-white/10"
          @keydown.esc.stop="closeSearch"
        >
          <div class="flex items-center gap-1 border-b border-default px-3 py-2 sm:px-4">
            <UIcon name="i-heroicons-magnifying-glass" class="size-5 shrink-0 text-muted" />
            <input
              ref="fieldRef"
              v-model="query"
              type="search"
              enterkeyhint="search"
              autocomplete="off"
              autocorrect="off"
              spellcheck="false"
              class="min-w-0 flex-1 bg-transparent py-2.5 text-base text-highlighted outline-none placeholder:text-dimmed sm:text-[1.05rem]"
              placeholder="Caută un produs..."
              aria-label="Caută produse"
              @keydown.down.prevent="moveHighlight(1)"
              @keydown.up.prevent="moveHighlight(-1)"
              @keydown.enter.prevent="onEnter"
            >
            <button
              type="button"
              class="flex size-8 items-center justify-center rounded-full text-muted transition-colors hover:bg-muted hover:text-highlighted"
              aria-label="Închide"
              @click="closeSearch"
            >
              <UIcon name="i-heroicons-x-mark" class="size-5" />
            </button>
          </div>

          <div v-if="loading" class="h-0.5 overflow-hidden bg-muted">
            <div class="h-full w-full origin-left animate-pulse bg-brand-500" />
          </div>

          <div ref="bodyRef" class="min-h-0 flex-1 overflow-y-auto">
            <div v-if="query.trim().length < 2" class="flex flex-col items-center px-6 py-12 text-center">
              <div class="mb-4 flex size-16 items-center justify-center rounded-full bg-brand-50 dark:bg-brand-950/40">
                <UIcon name="i-heroicons-magnifying-glass" class="size-8 text-brand-600" />
              </div>
              <p class="text-sm text-muted">Scrie cel puțin două litere. Căutăm în nume, cod produs și descriere.</p>
              <p class="mt-4 flex items-center gap-1.5 text-xs text-dimmed">
                <kbd class="rounded-md bg-muted px-1.5 py-0.5 font-medium ring-1 ring-default">{{ shortcutMod }}</kbd>
                <span>+</span>
                <kbd class="rounded-md bg-muted px-1.5 py-0.5 font-medium ring-1 ring-default">K</kbd>
              </p>
            </div>

            <div v-else-if="loading && !groups.length" class="space-y-2 p-3">
              <div v-for="n in 5" :key="n" class="flex items-center gap-3 px-2 py-2">
                <USkeleton class="size-11 rounded-xl" />
                <div class="flex-1 space-y-2">
                  <USkeleton class="h-4 w-2/3 rounded" />
                  <USkeleton class="h-3 w-1/2 rounded" />
                </div>
              </div>
            </div>

            <div v-else-if="!groups.length" class="flex flex-col items-center px-6 py-12 text-center">
              <div class="mb-4 flex size-16 items-center justify-center rounded-full bg-muted">
                <UIcon name="i-heroicons-magnifying-glass" class="size-8 text-dimmed" />
              </div>
              <p class="font-semibold text-highlighted">Nu am găsit rezultate</p>
              <p class="mt-1 text-sm text-muted">Nimic pentru „{{ query.trim() }}”. Încearcă un nume, cod produs sau descriere.</p>
            </div>

            <div v-else class="py-2">
              <section v-for="group in groups" :key="group.type" class="mb-1">
                <p class="px-4 pb-1 pt-2 text-[11px] font-bold uppercase tracking-wider text-muted">
                  {{ group.label }}
                </p>
                <ul>
                  <li v-for="item in group.items" :key="item.id">
                    <NuxtLink
                      :data-search-id="item.id"
                      :to="shopPath(item.href)"
                      class="mx-2 mb-0.5 flex items-center gap-3 rounded-xl px-2 py-2 transition-colors"
                      :class="item.id === highlightedId
                        ? 'bg-brand-50 dark:bg-brand-950/50'
                        : 'hover:bg-muted'"
                      @mouseenter="highlightedId = item.id"
                      @click="closeSearch"
                    >
                      <div class="size-11 shrink-0 overflow-hidden rounded-xl bg-muted ring-1 ring-black/5">
                        <AppImage
                          v-if="item.image"
                          :src="item.image"
                          :alt="item.title"
                          preset="thumbnail"
                          width="44"
                          height="44"
                          sizes="44px"
                          loading="lazy"
                          class="h-full w-full object-cover"
                        />
                        <div
                          v-else
                          class="flex h-full w-full items-center justify-center"
                          :class="typeMeta(item.type).bg"
                        >
                          <UIcon :name="typeMeta(item.type).icon" class="size-5" :class="typeMeta(item.type).color" />
                        </div>
                      </div>
                      <div class="min-w-0 flex-1">
                        <p class="truncate text-sm font-medium text-highlighted">
                          <span
                            v-for="(part, i) in highlightParts(item.title, query)"
                            :key="`${item.id}-t-${i}`"
                            :class="part.hit ? 'rounded-sm bg-brand-500/20 px-px' : ''"
                          >{{ part.text }}</span>
                        </p>
                        <p v-if="item.subtitle || item.hitIn" class="mt-0.5 truncate text-xs text-muted">
                          <span
                            v-if="searchHitWhere(item.hitIn)"
                            class="mr-1.5 inline-block text-[10px] font-bold uppercase tracking-wide text-brand-600"
                          >
                            {{ searchHitWhere(item.hitIn) }}
                          </span>
                          <span
                            v-for="(part, i) in highlightParts(item.subtitle || '', query)"
                            :key="`${item.id}-s-${i}`"
                            :class="part.hit ? 'rounded-sm bg-brand-500/20 px-px text-highlighted' : ''"
                          >{{ part.text }}</span>
                        </p>
                      </div>
                      <div class="shrink-0 text-right">
                        <p
                          v-if="item.type === 'product' && item.price != null"
                          class="text-sm font-semibold"
                          :class="pricingFor(item).hasDiscount ? 'text-rose-600 dark:text-rose-400' : 'text-highlighted'"
                        >
                          {{ formatPrice(pricingFor(item).displayPrice) }}
                        </p>
                        <span
                          class="mt-0.5 inline-flex rounded-full px-2 py-0.5 text-[10px] font-semibold"
                          :class="typeMeta(item.type).chip"
                        >
                          {{ item.typeLabel }}
                        </span>
                      </div>
                    </NuxtLink>
                  </li>
                </ul>
              </section>
            </div>
          </div>

          <button
            v-if="query.trim().length >= 2"
            type="button"
            class="flex w-full items-center justify-center gap-1.5 border-t border-default px-4 py-3 text-sm font-medium text-brand-600 transition-colors hover:bg-muted"
            @click="goToAllResults"
          >
            Vezi toate rezultatele pentru „{{ query.trim() }}”
            <UIcon name="i-heroicons-arrow-right" class="size-4" />
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import type { SiteSearchResponse, SiteSearchResult, SiteSearchResultType } from '~/types/search'
import { getProductCardPricing } from '~/utils/productCard'
import { highlightParts, searchHitWhere } from '~/utils/searchHighlight'
import { formatPrice } from '~/composables/useSeo'

const shopPath = useShopPath()
const router = useRouter()
const route = useRoute()
const { get } = useApi()
const { open, query, openSearch, closeSearch } = useSiteSearch()

const fieldRef = ref<HTMLInputElement | null>(null)
const dialogRef = ref<HTMLElement | null>(null)
const bodyRef = ref<HTMLElement | null>(null)
const results = ref<SiteSearchResult[]>([])
const loading = ref(false)
const highlightedId = ref('')
const shortcutMod = ref('Ctrl')

let abort: AbortController | null = null

const MIN_CHARS = 2

const TYPE_ORDER: SiteSearchResultType[] = ['product', 'category', 'brand', 'page', 'menu']
const TYPE_LABELS: Record<SiteSearchResultType, string> = {
  product: 'Produse',
  category: 'Categorii',
  brand: 'Branduri',
  page: 'Pagini',
  menu: 'Meniu',
}

const TYPE_META: Record<SiteSearchResultType, { icon: string, color: string, bg: string, chip: string }> = {
  product: {
    icon: 'i-heroicons-device-phone-mobile',
    color: 'text-brand-600',
    bg: 'bg-brand-50 dark:bg-brand-950/40',
    chip: 'bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300',
  },
  category: {
    icon: 'i-heroicons-squares-2x2',
    color: 'text-violet-600',
    bg: 'bg-violet-50 dark:bg-violet-950/40',
    chip: 'bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300',
  },
  brand: {
    icon: 'i-heroicons-tag',
    color: 'text-amber-600',
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    chip: 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
  },
  page: {
    icon: 'i-heroicons-document-text',
    color: 'text-sky-600',
    bg: 'bg-sky-50 dark:bg-sky-950/40',
    chip: 'bg-sky-50 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300',
  },
  menu: {
    icon: 'i-heroicons-link',
    color: 'text-slate-600',
    bg: 'bg-slate-100 dark:bg-slate-800',
    chip: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  },
}

const groups = computed(() => {
  const map = new Map<SiteSearchResultType, SiteSearchResult[]>()
  for (const item of results.value) {
    const list = map.get(item.type) || []
    list.push(item)
    map.set(item.type, list)
  }
  return TYPE_ORDER
    .filter((type) => map.has(type))
    .map((type) => ({
      type,
      label: TYPE_LABELS[type],
      items: map.get(type)!,
    }))
})

const flatItems = computed(() => groups.value.flatMap((group) => group.items))

function typeMeta(type: SiteSearchResultType) {
  return TYPE_META[type] || TYPE_META.page
}

function pricingFor(item: SiteSearchResult) {
  return getProductCardPricing({
    price: item.price ?? 0,
    salePrice: item.salePrice,
  })
}

async function focusField() {
  await nextTick()
  fieldRef.value?.focus()
  fieldRef.value?.select()
}

const runSearch = useDebounceFn(async (value: string) => {
  abort?.abort()
  const q = value.trim()
  if (q.length < MIN_CHARS) {
    results.value = []
    loading.value = false
    highlightedId.value = ''
    return
  }

  const controller = new AbortController()
  abort = controller
  loading.value = true
  try {
    const response = await get<SiteSearchResponse>('/search', {
      params: { q },
      signal: controller.signal,
    })
    if (controller.signal.aborted) return
    results.value = response.data?.results || []
    highlightedId.value = results.value[0]?.id || ''
  }
  catch (error) {
    if (controller.signal.aborted) return
    const code = typeof error === 'object' && error && 'code' in error ? String((error as { code?: string }).code) : ''
    if (code === 'ERR_CANCELED') return
    results.value = []
    highlightedId.value = ''
  }
  finally {
    if (abort === controller) loading.value = false
  }
}, 180)

function moveHighlight(delta: number) {
  const items = flatItems.value
  if (!items.length) return
  const current = items.findIndex((item) => item.id === highlightedId.value)
  const next = current < 0
    ? (delta > 0 ? 0 : items.length - 1)
    : (current + delta + items.length) % items.length
  highlightedId.value = items[next].id
  nextTick(() => {
    const el = bodyRef.value?.querySelector(`[data-search-id="${CSS.escape(items[next].id)}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  })
}

function onEnter() {
  const trimmed = query.value.trim()
  if (!trimmed) return
  const current = flatItems.value.find((item) => item.id === highlightedId.value)
  if (current) {
    closeSearch()
    router.push(shopPath(current.href))
    return
  }
  goToAllResults()
}

function goToAllResults() {
  const trimmed = query.value.trim()
  if (!trimmed) return
  closeSearch()
  router.push({ path: shopPath('/cautare'), query: { q: trimmed } })
}

function onGlobalKeydown(event: KeyboardEvent) {
  if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'k') return
  event.preventDefault()
  if (open.value) {
    focusField()
    return
  }
  openSearch()
}

watch(query, (value) => {
  if (!open.value) return
  if (value.trim().length >= MIN_CHARS) loading.value = true
  runSearch(value)
})

watch(open, (value) => {
  if (value) {
    document.documentElement.style.overflow = 'hidden'
    focusField()
    if (query.value.trim().length >= MIN_CHARS) runSearch(query.value)
    return
  }
  document.documentElement.style.overflow = ''
  abort?.abort()
})

watch(() => route.fullPath, () => {
  closeSearch()
})

onMounted(() => {
  shortcutMod.value = /Mac|iPhone|iPad/i.test(navigator.platform) ? '⌘' : 'Ctrl'
  window.addEventListener('keydown', onGlobalKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onGlobalKeydown)
  abort?.abort()
  document.documentElement.style.overflow = ''
})
</script>

<style scoped>
.site-search-fade-enter-active,
.site-search-fade-leave-active {
  transition: opacity 0.16s ease;
}
.site-search-fade-enter-active .relative,
.site-search-fade-leave-active .relative {
  transition: transform 0.16s ease, opacity 0.16s ease;
}
.site-search-fade-enter-from,
.site-search-fade-leave-to {
  opacity: 0;
}
.site-search-fade-enter-from .relative,
.site-search-fade-leave-to .relative {
  transform: translateY(-8px);
  opacity: 0;
}
</style>
